import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Image,
  Input,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  artesanos: any[];
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

const isImagePath = (value: string) => /\.(png|jpe?g|webp|gif)$/i.test(value);

const FormularioProducto: React.FC<Props> = ({ modo, data, artesanos, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<any>({
    artesanoId: '',
    nombre: '',
    tipo: '',
    descripcion: '',
    materiales: '',
    precioRef: '',
    disponibilidad: '',
  });
  const [guardando, setGuardando] = useState(false);
  const [fotosExistentes, setFotosExistentes] = useState<string[]>([]);
  const [fotosNuevas, setFotosNuevas] = useState<File[]>([]);

  useEffect(() => {
    if (data) {
      setForm({
        artesanoId: data.artesanoId || '',
        nombre: data.nombre || '',
        tipo: data.tipo || '',
        descripcion: data.descripcion || '',
        materiales: data.materiales || '',
        precioRef: data.precioRef || '',
        disponibilidad: data.disponibilidad || '',
      });
      setFotosExistentes(Array.isArray(data.fotos) ? data.fotos.filter(Boolean) : []);
      setFotosNuevas([]);
    }
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleFotosSeleccionadas = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setFotosNuevas((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!form.artesanoId || !form.nombre) return;
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append('artesanoId', String(Number(form.artesanoId)));
      fd.append('nombre', form.nombre || '');
      fd.append('tipo', form.tipo || '');
      fd.append('descripcion', form.descripcion || '');
      fd.append('materiales', form.materiales || '');
      fd.append('precioRef', form.precioRef || '');
      fd.append('disponibilidad', form.disponibilidad || '');
      fd.append('fotosExistentes', JSON.stringify(fotosExistentes));
      fotosNuevas.forEach((file) => fd.append('fotos', file));

      if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/turismo/productos/${data.id}`, fd);
      } else {
        await axiosInstance.post('/turismo/productos', fd);
      }
      onGuardado && onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  const disabled = modo === 'view';

  return (
    <Box>
      <Stack spacing={5}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired>
            <FormLabel>Artesano</FormLabel>
            <Select value={form.artesanoId} onChange={(e) => handleChange('artesanoId', e.target.value)} isDisabled={disabled}>
              <option value="">Selecciona...</option>
              {artesanos.map((a) => (
                <option key={a.id} value={a.id}>{a.nombres} {a.apellidos}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Nombre</FormLabel>
            <Input value={form.nombre} onChange={(e) => handleChange('nombre', e.target.value)} isDisabled={disabled} />
          </FormControl>
          <FormControl>
            <FormLabel>Tipo</FormLabel>
            <Input value={form.tipo} onChange={(e) => handleChange('tipo', e.target.value)} isDisabled={disabled} />
          </FormControl>
          <FormControl>
            <FormLabel>Precio referencia</FormLabel>
            <Input type="number" value={form.precioRef} onChange={(e) => handleChange('precioRef', e.target.value)} isDisabled={disabled} />
          </FormControl>
          <FormControl>
            <FormLabel>Disponibilidad</FormLabel>
            <Input value={form.disponibilidad} onChange={(e) => handleChange('disponibilidad', e.target.value)} isDisabled={disabled} />
          </FormControl>
          <FormControl>
            <FormLabel>Materiales</FormLabel>
            <Textarea value={form.materiales} onChange={(e) => handleChange('materiales', e.target.value)} isDisabled={disabled} />
          </FormControl>
          <FormControl gridColumn={{ md: '1 / -1' }}>
            <FormLabel>Descripcion</FormLabel>
            <Textarea value={form.descripcion} onChange={(e) => handleChange('descripcion', e.target.value)} isDisabled={disabled} rows={3} />
          </FormControl>
        </SimpleGrid>

        <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50">
          <Text fontWeight="bold" color="#5b182b" mb={3}>Fotos del producto (archivo)</Text>
          {!disabled && (
            <FormControl mb={4}>
              <FormLabel>Subir fotos</FormLabel>
              <Input type="file" accept="image/*" multiple onChange={handleFotosSeleccionadas} p={1} />
              <FormHelperText>Sube imágenes del producto, no URLs.</FormHelperText>
            </FormControl>
          )}

          <Box mb={4}>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>Fotos registradas</Text>
            {fotosExistentes.length === 0 ? (
              <Text fontSize="sm" color="gray.500">Sin fotos registradas.</Text>
            ) : (
              <Wrap>
                {fotosExistentes.map((foto) => (
                  <WrapItem key={foto}>
                    <Tag colorScheme="teal" borderRadius="full">
                      <TagLabel><Link href={resolveAssetUrl(foto)} isExternal>{foto.split('/').pop()}</Link></TagLabel>
                      {!disabled && <TagCloseButton onClick={() => setFotosExistentes((prev) => prev.filter((f) => f !== foto))} />}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>

          {fotosExistentes.filter(isImagePath).length > 0 && (
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={4}>
              {fotosExistentes.filter(isImagePath).map((foto) => (
                <Box key={`${foto}-img`} borderWidth="1px" borderRadius="md" overflow="hidden">
                  <Image src={resolveAssetUrl(foto)} alt={foto} h="120px" w="100%" objectFit="cover" />
                </Box>
              ))}
            </SimpleGrid>
          )}

          <Box>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>Nuevas fotos por subir</Text>
            {fotosNuevas.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No has seleccionado archivos nuevos.</Text>
            ) : (
              <Wrap>
                {fotosNuevas.map((file, index) => (
                  <WrapItem key={`${file.name}-${index}`}>
                    <Tag colorScheme="blue" borderRadius="full">
                      <TagLabel>{file.name}</TagLabel>
                      {!disabled && <TagCloseButton onClick={() => setFotosNuevas((prev) => prev.filter((_, i) => i !== index))} />}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>
        </Box>
      </Stack>

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">Guardar</Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioProducto;
